import { Link, useLocation } from "react-router-dom";
import { Type } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useGrandparent } from "@/lib/GrandparentContext";
import { LOGO_URL } from "@/lib/data";
import { PoweredBySocialPulse } from "@/components/site/SocialPulseLink";
import NetworkStrip from "@/components/site/NetworkStrip";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const gp = useGrandparent();

  return (
    <div className="grain relative min-h-screen bg-[color:var(--parchment)] text-[color:var(--ink)]">
      <header
        className="sticky top-0 z-40 border-b border-[color:var(--ink-line)] bg-[color:var(--parchment)]/90 backdrop-blur"
        data-testid="app-header"
      >
        <NetworkStrip />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-3 flex items-center justify-between gap-6">
          <div className="flex flex-col items-start shrink-0">
            <Link to="/" data-testid="app-logo">
              <img src={LOGO_URL} alt="FamilyTree.Team" className="h-16 md:h-20 w-auto" draggable="false" />
            </Link>
            <PoweredBySocialPulse className="mt-0.5 font-sans-ui text-[8px] tracking-[0.14em] uppercase text-[color:var(--sepia-warm)]/75" />
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-heritage-caps tracking-[0.12em]">
            <Link
              to="/app"
              data-testid="app-nav-dashboard"
              className={`link-underline ${location.pathname === "/app" ? "text-[color:var(--rust)]" : "text-[color:var(--sepia)]"}`}
            >
              DASHBOARD
            </Link>
            <Link
              to="/app/journal"
              data-testid="app-nav-journal"
              className={`link-underline ${location.pathname === "/app/journal" ? "text-[color:var(--rust)]" : "text-[color:var(--sepia)]"}`}
            >
              MY LIFE
            </Link>
            <Link
              to="/app/interview"
              data-testid="app-nav-interview"
              className={`link-underline ${location.pathname === "/app/interview" ? "text-[color:var(--rust)]" : "text-[color:var(--sepia)]"}`}
            >
              INTERVIEW
            </Link>
            <Link
              to="/app/missing"
              data-testid="app-nav-missing"
              className={`link-underline ${location.pathname === "/app/missing" ? "text-[color:var(--rust)]" : "text-[color:var(--sepia)]"}`}
            >
              MY CASES
            </Link>
            {user?.role === "admin" && (
              <>
                <Link
                  to="/app/moderation"
                  data-testid="app-nav-moderation"
                  className={`link-underline ${location.pathname === "/app/moderation" ? "text-[color:var(--rust)]" : "text-[color:var(--sepia)]"}`}
                >
                  REVIEW DESK
                </Link>
                <Link
                  to="/app/sso-rollout"
                  data-testid="app-nav-sso-rollout"
                  className={`link-underline ${location.pathname === "/app/sso-rollout" ? "text-[color:var(--rust)]" : "text-[color:var(--sepia)]"}`}
                >
                  SSO ROLLOUT
                </Link>
              </>
            )}
            <Link
              to="/"
              data-testid="app-nav-editorial"
              className="link-underline text-[color:var(--ink-soft)]"
            >
              ARCHIVE SITE
            </Link>
          </nav>

          <div className="flex items-center gap-4 text-sm">
            <button
              data-testid="grandparent-toggle"
              onClick={gp?.toggle}
              title={gp?.on ? "Switch back to standard type" : "Larger type and bigger buttons"}
              className={`inline-flex items-center gap-1.5 border px-3 py-2 transition-colors ${
                gp?.on
                  ? "border-[color:var(--rust)] bg-[color:var(--rust)] text-[color:var(--parchment)]"
                  : "border-[color:var(--sepia)]/40 text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)]"
              }`}
            >
              <Type size={14} />
              <span className="hidden sm:inline text-[11px] font-heritage-caps tracking-[0.12em]">
                {gp?.on ? "EASY READING ON" : "EASY READING"}
              </span>
            </button>
            {user && user !== false ? (
              <>
                <span
                  data-testid="app-current-user"
                  className="hidden sm:inline text-[color:var(--ink-soft)] font-italic-serif italic"
                >
                  {user.name}
                </span>
                <button
                  data-testid="app-logout-btn"
                  onClick={logout}
                  className="btn-ghost !py-2 !px-4 !text-[13px]"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/app/login" data-testid="app-login-link" className="link-underline text-[color:var(--sepia)]">
                  Sign in
                </Link>
                <Link
                  to="/app/register"
                  data-testid="app-register-link"
                  className="btn-wood !py-2 !px-4 !text-[13px]"
                >
                  Create archive
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-[color:var(--ink-line)] mt-24 py-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[color:var(--ink-soft)]">
          <div className="font-italic-serif italic">
            "A family is a story that keeps rewriting itself."
          </div>
          <div className="overline">FamilyTree.Team · Phase I · Est. 2026</div>
        </div>
      </footer>
    </div>
  );
}
